/**
 * Based on pattern from https://github.com/jaredLunde/react-hook/tree/master/packages/resize-observer
 *
 * As cited there, https://groups.google.com/a/chromium.org/g/blink-dev/c/z6ienONUb5A/m/F5-VcUZtBAAJ
 * explains how multiple ResizeObservers on a page can have reduced
 * performance.
 *
 * This package only ever creates one ResizeObserver that will be shared across
 * all `useResizeObserver` instances. However, it does it in a way that only
 * the relevant callbacks are called when certain targets resize.
 */
import { useEffect } from "react";
class MockResizeObserver {
  observe() {}
  unobserve() {}
  disconnect() {}
}
let memoizedResizeObserverProxy;
function getMemoizedResizeObserverProxy() {
  if (!memoizedResizeObserverProxy) {
    memoizedResizeObserverProxy = createResizeObserverProxy();
  }
  return memoizedResizeObserverProxy;
}
function createResizeObserverProxy() {
  /**
   * Store all the callbacks in a Map where the callback functions are the
   * keys and the values are a Set of all the targets the callback applies
   * to. This is how ResizeObserver is structured: it can apply the given
   * callback to multiple observed targets.
   */
  const callbacksMap = new Map();
  /**
   * Create a single shared ResizeObserver. It's callback will handle looping
   * through the callbacksMap and calling any relevant callbacks supplied by
   * consumers.
   */
  function callback(entries) {
    callbacksMap.forEach((targetSet, callback) => {
      /**
       * Find the ResizeObserver's entries that have targets matching the
       * current loop's callback:
       */
      const entriesForThisCallback = entries.filter(entry => targetSet.has(entry.target));
      if (entriesForThisCallback.length > 0) {
        callback(entriesForThisCallback);
      }
    });
  }
  try {
    // Jest doesn't support ResizeObserver
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(callback);
    } else {
      resizeObserver = new MockResizeObserver(callback);
    }
    return {
      resizeObserver,
      add(callback, target, options) {
        if (target) {
          resizeObserver.observe(target, options);
          /** Create a Set for the callback's targets if it doesn't exist. */
          if (!callbacksMap.has(callback)) {
            callbacksMap.set(callback, new Set());
          }
          /** Now add the given target to the targetSet */
          const targetSet = callbacksMap.get(callback);
          targetSet.add(target);
        }
      },
      remove(callback, target) {
        if (target) {
          /** Get the targetSet for the callback if it exists. */
          const targetSet = callbacksMap.get(callback) ?? new Set();
          targetSet.delete(target);
          /** Remove the callback from the Map if it has no more targets. */
          if (targetSet.size === 0) {
            callbacksMap.delete(callback);
          }
          /**
           * Unobserve the target if it is no longer associated with any
           * callbacks. */
          if (!Array.from(callbacksMap.values()).some(targetSet => targetSet.has(target))) {
            resizeObserver.unobserve(target);
          }
        }
      }
    };
  } catch (error) {
    console.warn("eden-resize-observer could not create a ResizeObserver:", error);
  }
}
export default function useResizeObserver(callback, refOrElement, options) {
  const proxy = getMemoizedResizeObserverProxy();
  useEffect(() => {
    if (!callback || !refOrElement) {
      console.error("eden-resize-observer:", new TypeError("The `callback` and `target` arguments are required."));
      return;
    }
    if (proxy) {
      const target = refOrElement && "current" in refOrElement ? refOrElement.current : refOrElement;
      proxy.add(callback, target, options);
      return () => proxy.remove(callback, target);
    }
  }, [callback, options, proxy, refOrElement]);
}